// Persistence: IndexedDB primary, LocalStorage fallback (AGENT.md §1).
// Everything stored is plain JSON so dev export/import is a straight passthrough.

import type { GameState } from './types';

const DB_NAME = 'clayfarm';
const DB_VERSION = 1;
const STORE_NAME = 'saves';
const SAVE_KEY = 'primary';
const LS_KEY = 'clayfarm_save_primary';

// --- Save-payload envelope (schema-blocker fix) --------------------------
//
// `DB_VERSION` above is only the IndexedDB object-store version. The cloud
// `saves` table and every local save store the whole GameState as JSON
// (AUTH-BACKEND.md §2), and that payload needed its own version so new
// fields can be added without every existing save loading with `undefined`
// holes. `saveVersion` is that number; `migrateSave` is the one entry point
// that decides whether a payload may be used at all.

/** Current save-payload envelope version. Bump when the payload shape breaks. */
export const CURRENT_SAVE_VERSION = 2;

/**
 * The version at which the September 2026 refocus landed. Everything below it
 * describes a game that no longer exists — salvage, drops, schematics, fast
 * mode, a title screen, a pannable camera — and there is no migration worth
 * writing for it. Pre-launch saves are disposable (RELEASE-PLAN.md D3), so a v1
 * payload is discarded whole and the player starts a fresh farm.
 */
const FIRST_REFOCUS_VERSION = 2;

/**
 * Thrown by `migrateSave` for a payload older than the refocus. Distinct from
 * `FutureSaveVersionError` on purpose: a stale save is the *expected* state of
 * every save in the wild right now, and it is thrown away on purpose. A future
 * one is refused so nothing is lost.
 */
export class StaleSaveVersionError extends Error {
  constructor(
    public readonly foundVersion: number,
    public readonly supportedVersion: number = CURRENT_SAVE_VERSION,
  ) {
    super(
      `This save is from before Clay Farm's big update (v${foundVersion}); this build ` +
        `starts a fresh farm at v${supportedVersion}.`,
    );
    this.name = 'StaleSaveVersionError';
  }
}

/**
 * Thrown by `migrateSave` when the payload's `saveVersion` is higher than
 * this build understands — e.g. a farm played on a newer build, synced down
 * to a device still running this one. Refusing beats silently dropping the
 * fields we don't recognise, which is how a two-device player loses a farm.
 */
export class FutureSaveVersionError extends Error {
  constructor(
    public readonly foundVersion: number,
    public readonly supportedVersion: number = CURRENT_SAVE_VERSION,
  ) {
    super(
      `This save is from a newer version of Clay Farm (v${foundVersion}) than this ` +
        `build understands (v${supportedVersion}). Update the game before loading it ` +
        `so nothing gets lost.`,
    );
    this.name = 'FutureSaveVersionError';
  }
}

/**
 * Decides whether a raw persisted payload — a parsed IndexedDB/localStorage row,
 * or a cloud `saves.save_json` blob — may be loaded, and returns it as a
 * current-shape `GameState`.
 *
 * Pure and synchronous: no IndexedDB, localStorage, or network access, so it
 * is unit-testable outside a browser.
 *
 * There is no migration ladder any more. A payload with no `saveVersion` is
 * treated as v0, and so is anything under the refocus: both are thrown away
 * rather than merged, because every field they carried belonged to a system
 * that has been deleted. A payload from a *newer* build throws
 * `FutureSaveVersionError` instead, so a two-device player is never silently
 * truncated.
 */
export function migrateSave(raw: unknown, _opts: { locale?: string } = {}): GameState {
  if (raw === null || typeof raw !== 'object') {
    throw new Error('[persistence] not a Clay Farm save: payload is not an object');
  }

  const data = raw as Partial<GameState> & { saveVersion?: number };
  const version = typeof data.saveVersion === 'number' ? data.saveVersion : 0;

  if (version > CURRENT_SAVE_VERSION) {
    throw new FutureSaveVersionError(version);
  }
  if (version < FIRST_REFOCUS_VERSION) {
    throw new StaleSaveVersionError(version);
  }

  return data as GameState;
}

export interface SaveAdapter {
  readonly name: string;
  /**
   * True when the last `load()` threw a pre-refocus save away and returned
   * null. The store reads this once to decide whether to tell the player their
   * farm started fresh — it is the difference between "no save yet" (a brand
   * new player, who needs no notice) and "your save was too old to keep".
   */
  readonly lastLoadWasStale: boolean;
  load(): Promise<GameState | null>;
  save(state: GameState): Promise<void>;
  clear(): Promise<void>;
}

/**
 * Thrown when the stored bytes are not valid JSON at all — distinct from a
 * future-version refusal or an old-but-readable save. The adapter quarantines
 * the row on seeing this so the player gets a fresh farm instead of being
 * locked out of every boot by one bad write.
 */
export class CorruptSaveError extends Error {
  constructor(public readonly cause_: unknown) {
    super('[persistence] save payload is corrupt (invalid JSON); starting fresh');
    this.name = 'CorruptSaveError';
  }
}

function decodeSave(raw: string): GameState {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    throw new CorruptSaveError(err);
  }
  return migrateSave(parsed);
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function tx<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, mode);
    const request = run(transaction.objectStore(STORE_NAME));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

class IndexedDbAdapter implements SaveAdapter {
  readonly name = 'indexeddb';
  lastLoadWasStale = false;
  private db: IDBDatabase | null = null;

  private async handle(): Promise<IDBDatabase> {
    if (!this.db) this.db = await openDb();
    return this.db;
  }

  async load(): Promise<GameState | null> {
    const db = await this.handle();
    const raw = await tx<string | undefined>(db, 'readonly', (s) => s.get(SAVE_KEY));
    if (!raw) return null;
    try {
      return decodeSave(raw);
    } catch (err) {
      // A pre-refocus save is not a failure: it is every save in the wild
      // today. Throw it away so the player gets a fresh farm instead of a
      // half-migrated one, and remember that we did.
      if (err instanceof StaleSaveVersionError) {
        console.warn(err.message);
        this.lastLoadWasStale = true;
        await this.clear();
        return null;
      }
      // FutureSaveVersionError propagates: refusing beats wiping. A corrupt
      // row, though, would poison every boot forever — quarantine it once.
      if (err instanceof CorruptSaveError) {
        console.warn(err.message, err.cause_);
        await this.clear();
        return null;
      }
      throw err;
    }
  }

  async save(state: GameState): Promise<void> {
    const db = await this.handle();
    await tx(db, 'readwrite', (s) => s.put(JSON.stringify(state), SAVE_KEY));
  }

  async clear(): Promise<void> {
    const db = await this.handle();
    await tx(db, 'readwrite', (s) => s.delete(SAVE_KEY));
  }
}

class LocalStorageAdapter implements SaveAdapter {
  readonly name = 'localstorage';
  lastLoadWasStale = false;

  async load(): Promise<GameState | null> {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    try {
      return decodeSave(raw);
    } catch (err) {
      // A pre-refocus save is not a failure: it is every save in the wild
      // today. Throw it away so the player gets a fresh farm instead of a
      // half-migrated one, and remember that we did.
      if (err instanceof StaleSaveVersionError) {
        console.warn(err.message);
        this.lastLoadWasStale = true;
        await this.clear();
        return null;
      }
      if (err instanceof CorruptSaveError) {
        console.warn(err.message, err.cause_);
        this.clear();
        return null;
      }
      throw err;
    }
  }

  async save(state: GameState): Promise<void> {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  }

  async clear(): Promise<void> {
    localStorage.removeItem(LS_KEY);
  }
}

class MemoryAdapter implements SaveAdapter {
  readonly name = 'memory';
  lastLoadWasStale = false;
  private data: string | null = null;

  async load(): Promise<GameState | null> {
    if (!this.data) return null;
    try {
      return decodeSave(this.data);
    } catch (err) {
      // A pre-refocus save is not a failure: it is every save in the wild
      // today. Throw it away so the player gets a fresh farm instead of a
      // half-migrated one, and remember that we did.
      if (err instanceof StaleSaveVersionError) {
        console.warn(err.message);
        this.lastLoadWasStale = true;
        this.data = null;
        return null;
      }
      if (err instanceof CorruptSaveError) {
        console.warn(err.message, err.cause_);
        this.data = null;
        return null;
      }
      throw err;
    }
  }

  async save(state: GameState): Promise<void> {
    this.data = JSON.stringify(state);
  }

  async clear(): Promise<void> {
    this.data = null;
  }
}

/**
 * Picks the best storage available. IndexedDB can exist but throw in private
 * browsing, so it is probed with a real round-trip before being accepted.
 */
export async function createSaveAdapter(): Promise<SaveAdapter> {
  if (typeof indexedDB !== 'undefined') {
    try {
      const adapter = new IndexedDbAdapter();
      await adapter.load();
      return adapter;
    } catch (err) {
      // A future-version save is not "IndexedDB is unavailable" — falling
      // back would probe localStorage/memory next and could silently start
      // a fresh farm over a save this build simply can't read yet. Refuse
      // instead of masking it as a storage failure.
      if (err instanceof FutureSaveVersionError) throw err;
      console.warn('[persistence] IndexedDB unavailable, falling back', err);
    }
  }
  if (typeof localStorage !== 'undefined') {
    try {
      const adapter = new LocalStorageAdapter();
      await adapter.load();
      return adapter;
    } catch (err) {
      if (err instanceof FutureSaveVersionError) throw err;
      console.warn('[persistence] LocalStorage unavailable, using memory', err);
    }
  }
  return new MemoryAdapter();
}
